import React, { useState, useRef, useEffect } from 'react';
import { toast } from '../../../components/shared/toast';
import './RecodeLabelForm.css';

function RecodeLabelForm({ existingLabel, onSave, onCancel, t }) {
  const [labelName, setLabelName] = useState('');
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (existingLabel) {
      setLabelName(existingLabel.name || '');
      if (existingLabel.imageUrl) {
        setUploadedLogo({
          name: 'Existing Image',
          size: 0,
          previewUrl: existingLabel.imageUrl
        });
      }
    }
  }, [existingLabel]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (file.size > 5 * 1024 * 1024) {
      toast.warning(t('recodeLabels.form.imageFormatsInfo') || 'File size must be smaller than 5MB.');
      return false;
    }
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.warning('File must be PNG, JPG, JPEG or PDF only.');
      return false;
    }
    return true;
  };

  const processFile = (file) => {
    const isPDF = file.name.toLowerCase().endsWith('.pdf');
    if (isPDF) {
      setUploadedLogo({
        file,
        name: file.name,
        size: file.size,
        previewUrl: null,
        isPDF: true
      });
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedLogo({
          file,
          name: file.name,
          size: file.size,
          previewUrl: event.target.result,
          isPDF: false
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedLogo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReset = () => {
    setLabelName(existingLabel ? existingLabel.name : '');
    if (!existingLabel) handleRemoveFile();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!labelName.trim()) {
      toast.warning('Please enter record label name.');
      return;
    }

    const payload = {
      name: labelName.trim(),
      image_url: uploadedLogo ? uploadedLogo.previewUrl : null
    };

    onSave(payload);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return 'Uploaded';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="recode-label-form-wrapper">
      {/* Breadcrumbs Nav */}
      <div className="breadcrumb-nav">
        <span className="breadcrumb-link" onClick={onCancel} style={{ cursor: 'pointer' }}>
          Record Labels
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">
          {existingLabel ? 'Edit Record Label' : (t('recodeLabels.form.breadcrumb') || 'Add New Record Label')}
        </span>
      </div>

      {/* Header Row */}
      <div className="add-document-header-row">
        <div className="header-icon-box">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="3" />
          </svg>
        </div>
        <div className="header-text-container">
          <h1 className="add-document-title">
            {existingLabel ? 'Edit Record Label' : (t('recodeLabels.form.title') || 'Add New Record Label')}
          </h1>
          <p className="add-document-subtitle">
            {t('recodeLabels.form.subtitle') || 'Enter the record label details.'}
          </p>
        </div>
      </div>

      {/* Main Form Fields Card */}
      <form onSubmit={handleSubmit} className="form-card">
        {/* Record Label Name */}
        <div className="form-field-group">
          <label className="form-field-label">
            Record Label Name
            <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            className="form-text-input"
            placeholder="Enter record label name"
            value={labelName}
            onChange={(e) => setLabelName(e.target.value)}
          />
        </div>

        {/* Upload Logo File Input */}
        <div className="upload-container">
          <label className="form-field-label">
            Record Label Image / Logo
          </label>

          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div
            className={`upload-drop-zone ${isDragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <div className="upload-content-wrapper">
              <div className="upload-icon-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="upload-main-text">
                {t('recodeLabels.form.dragDropText') || 'Drag & drop your file here or'}
              </p>
              <span className="upload-or-text">or</span>
              <button type="button" className="choose-file-btn">
                {t('recodeLabels.form.chooseFile') || 'Choose File'}
              </button>
            </div>
          </div>

          <div className="recode-upload-sub-text-row">
            <span>{t('recodeLabels.form.imageFormatsInfo') || 'Supported formats: PNG, JPG, JPEG, PDF (Max 5MB)'}</span>
          </div>

          {/* Uploaded Logo Preview details */}
          {uploadedLogo && (
            <div className="uploaded-file-card">
              <div className="file-info-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {uploadedLogo.previewUrl && !uploadedLogo.isPDF ? (
                  <div style={{ width: '40px', height: '40px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                    <img src={uploadedLogo.previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                ) : (
                  <span className="file-icon-red">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </span>
                )}
                <div>
                  <span className="file-name-text">{uploadedLogo.name}</span>
                  <span className="file-size-text">{formatFileSize(uploadedLogo.size)}</span>
                </div>
              </div>
              <button
                type="button"
                className="remove-file-action"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                aria-label="Remove uploaded file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="form-actions-footer">
          <button
            type="button"
            className="form-btn btn-outlined"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="form-btn btn-primary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span>
              {existingLabel ? 'Update Record Label' : (t('recodeLabels.form.save') || 'Save Record Label')}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default RecodeLabelForm;
